import mixins from 'vue-typed-mixins';
import _ from 'lodash';

import { E取込データ種別 } from '@/openapi/enum';
import {
  取込Api,
  取込FileListItemResponse,
  取込Pdfカテゴリ決算書ListItemRequest,
  取込Pdf決算書ListItemRequest,
  帳票ListItemRequest,
} from '@/openapi/user';

import Pdf決算書TabComponent from '@/components/取込/データ確認/pdf/決算書/Pdf決算書Tab.vue';
import C取込Error from '@/components/取込/error/取込Error.vue';
import { Pdf決算書Row, Pdf決算書Tab } from '@/components/取込/データ確認/pdf/決算書/Pdf決算書Tab';

import 取込Mixin, { Default分類 } from '@/entries/user/mixins/取込Mixin';
import UserApiUtility from '@/classes/common/UserApiUtility';

export default mixins(取込Mixin).extend({
  name: 'Pdf決算書Dialog',

  components: {
    Pdf決算書TabComponent,
    C取込Error,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data(): {
    pdf決算書Tabs: Pdf決算書Tab[];
  } {
    return {
      pdf決算書Tabs: [],
    };
  },

  computed: {
    tabs(): string[] {
      return this.pdf決算書Tabs.map((表) => this.get取込データ種別Label(表.取込データ種別));
    },
    pdf決算書Tab(): Pdf決算書Tab | undefined {
      return this.pdf決算書Tabs[this.tabIndex()];
    },
  },

  watch: {
    async visible(): Promise<void> {
      if (!this.visible) return;

      // 初期化
      this.setTabIndex(0, 0);
      this.pdf決算書Tabs = [];
      this.取込データ種別Errors = [];

      // 勘定科目、取込データ取得
      const isError = await this.load取込データs('決算書');

      // エラーがある場合はダイアログを閉じてreturn
      if (isError) {
        this.close();
        return;
      }

      // 取込データ設定
      this.pdf決算書Tabs = this.取込データs.map((d) => {
        const 取込データ種別 = d.取込データ種別!;
        const default分類 = this.getDefault分類(取込データ種別);

        return {
          取込データ種別,
          list: d.list!.map((item) => this.$_createPdf決算書Row(item, 取込データ種別, default分類)),
        };
      });
    },
  },

  methods: {
    close(): void {
      this.$emit('update:visible', false);
    },
    async save(): Promise<void> {
      this.取込データ種別Errors = [];
      this.pdf決算書Tabs.forEach((tab) => {
        const new勘定科目s = tab.list
          .filter((row) => row.isInMaster && row.request.勘定科目Pk === undefined)
          .map((row) => row.request.取込勘定科目!);

        tab.list.forEach((row, i) => {
          if (!row.isInMaster) {
            this.add取込Error(tab.取込データ種別, '勘定科目', 'マスタに存在しません', i);
          } else {
            // 追加される同名勘定科目の大分類/中分類が揃っているかチェック
            const 取込 = row.request.取込勘定科目!;
            const finded = new勘定科目s.find((row) => row.c勘定科目 === 取込.c勘定科目);
            if (finded !== undefined && (finded.c大分類 !== 取込.c大分類 || finded.c中分類 !== 取込.c中分類)) {
              this.add取込Error(tab.取込データ種別, '勘定科目', '大分類/中分類が異なる勘定科目は追加出来ません', i);
            }
          }

          if (!this.$_validateValue(row.strValue)) {
            this.add取込Error(tab.取込データ種別, '金額', '数字を入力してください', i);
          }
        });
      });

      // 入力チェック不可の場合はここでreturn
      if (this.取込データ種別Errors.length > 0) return;

      // 送信データ成形
      const requests: 取込Pdfカテゴリ決算書ListItemRequest[] = [];
      this.pdf決算書Tabs
        .filter((tab) => tab.list.length > 0)
        .forEach((tab) => {
          // データ成形
          const request: 取込Pdfカテゴリ決算書ListItemRequest = {
            cカテゴリ: this.getCカテゴリFrom取込データ種別(tab.取込データ種別),
            list: tab.list.map((row) => {
              row.request.value = this.$_convertInputToValue(row.strValue);
              return row.request;
            }),
          };

          // 重複するカテゴリがある場合は結合、なければ追加
          const duplicate = requests.find((req) => req.cカテゴリ === request.cカテゴリ);
          if (duplicate !== undefined) {
            duplicate.list = [...duplicate.list!, ...request.list!];
          } else {
            requests.push(request);
          }
        });

      requests.forEach((request) => {
        const list = request.list!;
        const unique科目名 = [...new Set(list.map((row) => row.取込勘定科目!.c勘定科目))];
        request.list = unique科目名.map((科目名) => {
          const filtered = list.filter((row) => row.取込勘定科目!.c勘定科目 === 科目名);
          const first = filtered[0];

          return {
            勘定科目Pk: first.勘定科目Pk,
            取込勘定科目: first.取込勘定科目,
            // 決算書と試算表で以下の部分しか差異がないので
            // 取込Pdf決算書ListItemRequest | 取込Pdf試算表ListItemRequest
            // とかで処理を纏めようとしたが、何とも上手くいかなかった
            value: _.sum(filtered.map((row) => row.value)),
          };
        });
      });

      // 送信
      await UserApiUtility.ogApi(取込Api).save取込Pdf決算書(this.計画書.pk, this.importInfo.年, requests);

      this.$emit('saved');
      this.close();
    },
    output決算書(): void {
      const createRequest = (tab: Pdf決算書Tab): 帳票ListItemRequest => {
        const templateRows = [
          this.outputConfig.template.header,
          ...new Array(tab.list.length).fill(this.outputConfig.template.row),
        ];
        const values = [
          [], // header
          ...tab.list.map((row) => [row.request.取込勘定科目?.c勘定科目, row.strValue, row.etc]),
        ];
        return {
          templateName: '取込決算書',
          sheetName: this.get取込データ種別Label(tab.取込データ種別),
          templateRows,
          values,
        };
      };

      this.outputXlsx(this.pdf決算書Tabs, createRequest);
    },
    $_createPdf決算書Row(
      item: 取込FileListItemResponse,
      取込データ種別: E取込データ種別,
      default分類: Default分類
    ): Pdf決算書Row {
      const request: 取込Pdf決算書ListItemRequest = {};
      const 勘定科目 = this.filter勘定科目(取込データ種別).find((勘) => 勘.c勘定科目 === item.勘定科目);
      this.set取込勘定科目(request, 勘定科目, item, default分類);

      return {
        selected勘定科目: null,
        strValue: item.values[0],
        etc: item.values[1],
        isInMaster: 勘定科目 !== undefined,
        request,
      };
    },
    $_validateValue(input: string): boolean {
      const value = this.$_convertInputToValue(input);
      return value === undefined || !isNaN(value);
    },
    $_convertInputToValue(input: string): number | undefined {
      return input !== '' ? Number(input.replaceAll(',', '')) : undefined;
    },
  },
});
